/**
 * UomSelect — the ONE picker for a unit-of-measure token.
 *
 * Every `uom` column in the ERP cluster stores a token from the global UoM
 * catalog (`shared/config/uom.config.json`, generated as `UOM_VALUES`), never
 * free text — a "pcs" typed on one line and an "each" on the next cannot be
 * reconciled at receipt or on the invoice. This control offers the catalog in
 * catalog order, labelled through `common:uom.*`, and emits the token.
 *
 * `allowEmpty` adds a leading "{{Product}}'s base unit" row that emits `null`:
 * a line whose `uom` is omitted takes the product's `base_uom` server-side, so
 * on a line editor the empty choice IS the default and must be pickable.
 *
 * The trigger carries the coarse-pointer 44px floor from `SelectTrigger`
 * itself; callers do not add it.
 */
import { useTranslation } from 'react-i18next';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import { UOM_VALUES, type Uom } from '../../../generated/config-enums/uom';

/**
 * Radix `Select` cannot carry an empty-string item value, so the "base unit"
 * row is spelled with a sentinel that is not a catalog token and mapped back to
 * `null` at the edge. Never a persisted value.
 */
const BASE_UNIT_VALUE = '__base_unit__';

export interface UomSelectProps {
  value: Uom | null;
  onChange: (token: Uom | null) => void;
  /** Offer a leading row meaning "the product's base unit" (emits `null`). */
  allowEmpty?: boolean;
  disabled?: boolean;
  id?: string;
  'data-testid'?: string;
  /** Visible field label; omit in a dense grid cell and pass `aria-label`. */
  label?: string;
  'aria-label'?: string;
  required?: boolean;
  /** Helper line under the control. */
  description?: string;
  size?: 'sm' | 'default';
  className?: string;
}

export function UomSelect({
  value,
  onChange,
  allowEmpty = false,
  disabled,
  id,
  'data-testid': dataTestId,
  label,
  'aria-label': ariaLabel,
  required,
  description,
  size = 'default',
  className,
}: UomSelectProps) {
  const { t } = useTranslation('common');

  const selectValue = value ?? (allowEmpty ? BASE_UNIT_VALUE : '');

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label htmlFor={id} required={required}>
            {label}
          </Label>
        </div>
      )}
      <Select
        value={selectValue}
        onValueChange={(next) => onChange(next === BASE_UNIT_VALUE ? null : (next as Uom))}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          data-testid={dataTestId}
          aria-label={ariaLabel}
          aria-required={required || undefined}
          className={cn(size === 'sm' && 'h-8 text-sm')}
        >
          <SelectValue placeholder={t('pickers.selectUom')} />
        </SelectTrigger>
        <SelectContent>
          {allowEmpty && (
            <SelectItem value={BASE_UNIT_VALUE}>{t('pickers.uomBaseUnit')}</SelectItem>
          )}
          {UOM_VALUES.map((token) => (
            <SelectItem key={token} value={token}>
              {t(`uom.${token}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}
